import { useCallback, useEffect, useRef, useState } from 'react';
import { AgentDefinition, ChatMessage, ToolCall } from '../types';
import {
  ConversationDto,
  MessageDto,
  createConversation,
  sendMessage as sendMessageApi,
  sendMessageStream,
} from '../api/conversations';
import { fmtTime } from '../utils/chatMessages';
import { getFriendlyErrorMessage } from '../utils/chatErrors';

export interface PersistedTurn {
  convId: string;
  optimisticUserId: string;
  userMessage: MessageDto;
  assistantMessage: MessageDto;
  toolCalls?: ToolCall[];
}

export interface UseStreamingChatOpts {
  getToken: () => Promise<string>;
  onConversationCreated: (conv: ConversationDto) => void;
  onOptimisticInsert: (convId: string, msg: ChatMessage) => void;
  onPersisted: (turn: PersistedTurn) => void;
  onError: (convId: string | null, msg: ChatMessage) => void;
}

export interface SendArgs {
  text: string;
  agent: AgentDefinition;
  conversationId: string | null;
  promptMode: string | null;
  model: string | null;
}

export function useStreamingChat(opts: UseStreamingChatOpts) {
  const { getToken, onConversationCreated, onOptimisticInsert, onPersisted, onError } = opts;

  const [sendingMsg, setSendingMsg] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingThinking, setStreamingThinking] = useState('');
  const [streamingTools, setStreamingTools] = useState<ToolCall[]>([]);
  const [streamingAgentId, setStreamingAgentId] = useState<string | null>(null);
  // Seconds the agent has been working, from the stream's `status` events.
  const [streamingElapsed, setStreamingElapsed] = useState<number | null>(null);

  // One state write per frame. Setting state per token re-renders the thread and
  // re-runs markdown parsing in every bubble, which locks up the UI.
  const rafRef = useRef<number | null>(null);
  const pendingContentRef = useRef<string | null>(null);
  // Guards against a duplicate in-flight send (e.g. a double Enter/click before
  // `sendingMsg` state commits) racing to create two conversations for one message.
  const sendInFlightRef = useRef(false);

  const cancelScheduledFlush = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingContentRef.current = null;
  }, []);

  const scheduleContentUpdate = useCallback((next: string) => {
    pendingContentRef.current = next;
    if (rafRef.current !== null) return; // flush already queued
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (pendingContentRef.current !== null) {
        setStreamingContent(pendingContentRef.current);
        pendingContentRef.current = null;
      }
    });
  }, []);

  useEffect(() => cancelScheduledFlush, [cancelScheduledFlush]);

  const send = useCallback(async ({ text, agent, conversationId, promptMode, model }: SendArgs) => {
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    const now = fmtTime(new Date().toISOString());
    const supportsStreaming = !!agent.endpoints?.chatStreamUrl;

    setSendingMsg(true);
    setStreamingElapsed(null);
    setStreamingContent('');
    setStreamingThinking('');
    setStreamingTools([]);
    setStreamingAgentId(agent.id);

    let convId = conversationId;
    const optimisticUser: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: now,
    };

    try {
      if (!convId) {
        const newConv = await createConversation(getToken, agent.id, text.slice(0, 60));
        convId = newConv.id;
        onConversationCreated(newConv);
      }
      onOptimisticInsert(convId, optimisticUser);

      if (supportsStreaming) {
        // Stream-with-retract: tokens go into the bubble live; if a tool_call
        // arrives, the `pendingSinceCommit` slice is moved to the thinking
        // panel and removed from the bubble. tool_result/done commit
        // implicitly (clear pending without retracting).
        let accumulated = '';
        let pendingSinceCommit = '';
        let thinkingBuf = '';
        const tools: ToolCall[] = [];

        await sendMessageStream(
          getToken, convId, agent.id, text, promptMode, model,
          (event) => {
            switch (event.type) {
              case 'token':
                accumulated += event.delta;
                pendingSinceCommit += event.delta;
                scheduleContentUpdate(accumulated);
                break;
              case 'status':
                setStreamingElapsed(event.elapsed_s);
                break;
              case 'thinking_retract':
                if (pendingSinceCommit) {
                  thinkingBuf += (thinkingBuf ? '\n\n' : '') + pendingSinceCommit;
                  accumulated = accumulated.slice(0, accumulated.length - pendingSinceCommit.length);
                  pendingSinceCommit = '';
                  scheduleContentUpdate(accumulated);
                  setStreamingThinking(thinkingBuf);
                }
                break;
              case 'tool_call':
                pendingSinceCommit = '';
                tools.push({
                  id: event.id,
                  name: event.name,
                  description: '',
                  status: 'running',
                  args: event.args,
                });
                setStreamingTools([...tools]);
                break;
              case 'tool_result': {
                pendingSinceCommit = '';
                // Match by id when available, otherwise fall back to most recent running tool of the same name.
                let idx = -1;
                if (event.tool_call_id) {
                  idx = tools.findIndex((t) => t.id === event.tool_call_id);
                }
                if (idx < 0) {
                  const reverseIdx = [...tools].reverse().findIndex((t) => t.name === event.name && t.status === 'running');
                  if (reverseIdx >= 0) idx = tools.length - 1 - reverseIdx;
                }
                if (idx >= 0) {
                  tools[idx] = {
                    ...tools[idx],
                    // A tool can fail inside a 200 response; showing it as a
                    // success made a partial answer look complete.
                    status: event.status === 'error' ? 'error' : 'success',
                    result: event.result,
                    preview: event.preview,
                  };
                  setStreamingTools([...tools]);
                }
                break;
              }
              case 'done':
                onPersisted({
                  convId: convId!,
                  optimisticUserId: optimisticUser.id,
                  userMessage: event.userMessage,
                  assistantMessage: event.assistantMessage,
                  toolCalls: tools,
                });
                break;
              case 'error':
                // sendMessageStream throws after this — handled in catch
                break;
            }
          },
        );
      } else {
        const { userMessage, assistantMessage } = await sendMessageApi(
          getToken, convId, agent.id, text, promptMode, model,
        );
        onPersisted({ convId, optimisticUserId: optimisticUser.id, userMessage, assistantMessage });
      }
    } catch (err) {
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        agentId: agent.id,
        content: `⚠️ ${getFriendlyErrorMessage(err)}`,
        timestamp: now,
      };
      onError(convId, errorMsg);
    } finally {
      sendInFlightRef.current = false;
      // Must precede the reset, or a queued frame resurrects the streaming bubble.
      cancelScheduledFlush();
      setSendingMsg(false);
      setStreamingElapsed(null);
      setStreamingContent('');
      setStreamingThinking('');
      setStreamingTools([]);
      setStreamingAgentId(null);
    }
  }, [
    getToken, onConversationCreated, onOptimisticInsert, onPersisted, onError,
    scheduleContentUpdate, cancelScheduledFlush,
  ]);

  return {
    sendingMsg,
    streamingElapsed,
    streamingContent,
    streamingThinking,
    streamingTools,
    streamingAgentId,
    send,
  };
}
